/**
 * One worker read for a screen: asks `type` with `payload` once the database is ready,
 * keeps the last answer, and asks again 250 ms after any sync-state broadcast (a save on
 * this device, a pulled page, a snapshot) or when the caller asks. `data` is undefined
 * while the first answer is on its way, null when the worker answered null (not found or
 * out of scope). `reload` resolves once its answer is in (release 20 review M8: a save that
 * awaits it closes and says so on what it saved).
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import { getDb } from '@/app/db'
import { useUi } from '@/app/store/ui'
import type { ReservedRequestType } from '@/db/protocol'

export interface WorkerRead<T> {
  data: T | null | undefined
  error: string | null
  /** Asks again; resolves once the answer (or the failure) is in. */
  reload: () => Promise<void>
}

const REFRESH_DEBOUNCE_MS = 250

export function useWorkerRead<T>(
  type: ReservedRequestType,
  payload: unknown,
  options: { live?: boolean; enabled?: boolean } = {},
): WorkerRead<T> {
  const key = `${type}|${JSON.stringify(payload ?? null)}`
  const enabled = options.enabled !== false
  const [state, setState] = useState<{
    key: string
    data: T | null | undefined
    error: string | null
  }>({ key, data: undefined, error: null })
  const loadRef = useRef<() => Promise<void>>(async () => {})

  useEffect(() => {
    if (!enabled) return
    let alive = true
    const db = getDb()
    const body = JSON.parse(key.slice(type.length + 1)) as unknown
    const load = () =>
      db.ready
        .then(() => db.requestRaw(type, body))
        .then(
          (result) => {
            if (alive) setState({ key, data: result as T | null, error: null })
          },
          (error: unknown) => {
            if (alive) {
              setState((prev) => ({
                key,
                data: prev.key === key ? prev.data : undefined,
                error: error instanceof Error ? error.message : String(error),
              }))
            }
          },
        )
    loadRef.current = load
    void load()
    let timer: ReturnType<typeof setTimeout> | null = null
    const unsubscribe =
      options.live === false
        ? () => {}
        : useUi.subscribe((current, previous) => {
            if (current.sync === previous.sync) return
            if (timer !== null) clearTimeout(timer)
            timer = setTimeout(() => {
              timer = null
              void load()
            }, REFRESH_DEBOUNCE_MS)
          })
    return () => {
      alive = false
      unsubscribe()
      if (timer !== null) clearTimeout(timer)
      loadRef.current = async () => {}
    }
  }, [key, type, enabled, options.live])

  const reload = useCallback(() => loadRef.current(), [])
  return state.key === key
    ? { data: state.data, error: state.error, reload }
    : { data: undefined, error: null, reload }
}
